import PropTypes from "prop-types";
import { 
  Box, 
  Divider, 
  ToggleButtonGroup, 
  ToggleButton,
  Typography
} from "@mui/material";
import { useState } from "react";

const ROWS_PER_COLUMN = 3;

export const MeasurementPanel = ({sides}) => {
  // Key-to-label conversion
  const capitalized = (phrase) => {
    return phrase
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  const sideLabelFromKey = (side) => {
    return capitalized(side);
  };
  const rowLabel = (key, label) => {
    if (label) { return label; }
    return capitalized(key);
  };

  // Measurement selection
  const [selected, setSelected] = useState(null);
  const forms = sides;
  // An unset or unknown selection falls back to the first side measured.
  const selectedForm = 
    forms.find((form) => form.side === selected) || forms[0];

  // Fallback component (renders nothing)
  if (!selectedForm) { return null; }

  // The panel component
  return (
    <Box
      sx={{
        width: 'fit-content',
        minWidth: 240,
        maxWidth: '100%',
        mx: 'auto',
        px: 1.5,
        py: 1.25,
        borderRadius: 3,
        backgroundColor: 'rgba(142, 142, 142, 0.4)',
        backdropFilter: 'blur(4px)'
      }}
    >
      {/* Side selector -- Shown only when `side` is available. */}
      {forms.length > 1 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={selectedForm.side}
              onChange={(_, next) => next && setSelected(next)}
              sx={{
                width: 160,
                backgroundColor: 'rgba(255,255,255,0.35)',
                borderRadius: 999,
                p: 0.25,
                '& .MuiToggleButton-root': {
                  flex: 1,
                  border: 0,
                  borderRadius: '999px !important',
                  py: 0.25,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&.Mui-selected': { 
                    backgroundColor: '#fff', 
                    boxShadow: 1,
                    '&:hover, &.Mui-focusVisible': { backgroundColor: '#fff' }
                  }
                }
              }}
            >
              {forms.map((form) => (
                <ToggleButton key={form.side} value={form.side}>
                  {sideLabelFromKey(form.side) || '-'}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Divider sx={{ my: 1 }} />
        </>
      )}

      {/* Horizontally scrollable measurement rows -- three per column */}
      <Box
        sx={{
          display: 'grid',
          justifyContent: 'safe center',
          gridAutoFlow: 'column',
          gridTemplateRows: 
            `repeat(
              ${Math.min(
                ROWS_PER_COLUMN, 
                selectedForm.measurements.length
              )},
              auto
            )`,
          gridAutoColumns: "max-content",
          columnGap: 2,
          rowGap: 0.25,
          overflowX: 'auto',
          overscrollBehaviorX: 'contain',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }}
      >
        {selectedForm.measurements.map((measurement) => (
          <Box
            key={measurement.key}
            sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
          >
            <Typography 
              variant="body2" 
              sx={{ fontWeight: 700, color: 'text.secondary' }}
            >
              {rowLabel(measurement.key, measurement.label)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", whiteSpace: "nowrap" }}
            >
              {`${measurement.value.toFixed(1)} mm`}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

MeasurementPanel.propTypes = {
  sides: PropTypes.array.isRequired
};
